import React, { useState, useRef, useEffect, useCallback } from 'react'
import { User, Phone, Mail, Search, UserPlus, MapPin } from 'lucide-react'
import { formatPhoneInput } from '../../utils/validation'

/**
 * CustomerLookup - Reusable customer auto-fill component
 *
 * Usage:
 *   <CustomerLookup
 *     customerName={customerName}
 *     customerPhone={customerPhone}
 *     customerEmail={customerEmail}
 *     customerCity={customerCity}
 *     customerState={customerState}
 *     country="India"                            // or "United States" — drives city/state suggestions
 *     onChange={(field, value) => { ... }}
 *     onCustomerLoaded={(customer) => { ... }}   // optional callback when existing customer is loaded
 *   />
 *
 * onChange field names: 'customerName' | 'customerPhone' | 'customerEmail' | 'customerCity' | 'customerState'
 */

const DEBOUNCE_MS = 500

const CustomerLookup = ({
  customerName = '',
  customerPhone = '',
  customerEmail = '',
  customerCity = '',
  customerState = '',
  country = 'India',
  onChange,
  onCustomerLoaded,
  compact = false,        // compact=true renders inline fields, compact=false renders full card
  stateOptions = [],      // pass your state <option> list if needed — no longer used, kept for backward compatibility
}) => {
  const [suggestions, setSuggestions] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [customerHighlight, setCustomerHighlight] = useState(-1)
  // eslint-disable-next-line no-unused-vars
  const [linkedCustomer, setLinkedCustomer] = useState(null) // customer that was auto-loaded
  const [activeField, setActiveField] = useState(null)       // 'phone' | 'email' | 'name'

  // Merged City/State field
  const joinLocation = (city, state) => [city, state].filter(Boolean).join(', ')
  const [locationText, setLocationText] = useState(() => joinLocation(customerCity, customerState))
  const [locationSuggestions, setLocationSuggestions] = useState([])
  const [showLocationDropdown, setShowLocationDropdown] = useState(false)
  const [locationHighlight, setLocationHighlight] = useState(-1)
  const locationDebounceRef = useRef(null)
  const locationDropdownRef = useRef(null)

  // Keep the merged text in sync when city/state change from outside (e.g. selectCustomer)
  useEffect(() => {
    setLocationText(joinLocation(customerCity, customerState))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCity, customerState])

  const debounceRef = useRef(null)
  const dropdownRef = useRef(null)

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target)) {
        setShowLocationDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const searchLocations = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setLocationSuggestions([])
      setShowLocationDropdown(false)
      return
    }
    try {
      const res = await fetch(`/api/utils/city-suggest?q=${encodeURIComponent(term)}&country=${encodeURIComponent(country)}`)
      const data = await res.json()
      if (data.success && data.data && data.data.length > 0) {
        setLocationSuggestions(data.data)
        setShowLocationDropdown(true)
        setLocationHighlight(-1)
      } else {
        setLocationSuggestions([])
        setShowLocationDropdown(false)
        setLocationHighlight(-1)
      }
    } catch (err) {
      console.error('CustomerLookup city-suggest error:', err)
    }
  }, [country])

  const handleLocationInput = (value) => {
    setLocationText(value)
    if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current)
    locationDebounceRef.current = setTimeout(() => searchLocations(value), DEBOUNCE_MS)
  }

  const selectLocation = (loc) => {
    setLocationText(joinLocation(loc.city, loc.state))
    onChange('customerCity', loc.city)
    onChange('customerState', loc.state)
    setLocationSuggestions([])
    setShowLocationDropdown(false)
    setLocationHighlight(-1)
  }

  const handleLocationBlur = () => {
    setTimeout(() => {
      // Only commit free-typed text back to city/state if nothing was already
      // committed via selectLocation (which sets city/state directly).
      const [typedCity, typedState] = locationText.split(',').map(s => s.trim())
      if (joinLocation(customerCity, customerState) !== locationText) {
        onChange('customerCity', typedCity || '')
        onChange('customerState', typedState || '')
      }
    }, 150)
  }

  const handleLocationKeyDown = (e) => {
    if (!showLocationDropdown || locationSuggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setLocationHighlight(i => (i + 1) % locationSuggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setLocationHighlight(i => (i <= 0 ? locationSuggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      if (locationHighlight >= 0) {
        e.preventDefault()
        selectLocation(locationSuggestions[locationHighlight])
      }
    } else if (e.key === 'Escape') {
      setShowLocationDropdown(false)
      setLocationHighlight(-1)
    }
  }

  const searchCustomers = useCallback(async (term) => {
    if (!term || term.trim().length < 3) {
      setSuggestions([])
      setShowDropdown(false)
      return
    }
    setIsSearching(true)
    try {
      const res = await fetch(`/api/customers/search?term=${encodeURIComponent(term)}&limit=8&activeOnly=true`, {
        credentials: 'include'
      })
      const data = await res.json()
      if (data.success && data.data && data.data.length > 0) {
        setSuggestions(data.data)
        setShowDropdown(true)
        setCustomerHighlight(-1)
      } else {
        setSuggestions([])
        setShowDropdown(false)
        setCustomerHighlight(-1)
      }
    } catch (err) {
      console.error('CustomerLookup search error:', err)
    } finally {
      setIsSearching(false)
    }
  }, [])

  const handleInput = (field, rawValue) => {
    const value = field === 'customerPhone' ? formatPhoneInput(rawValue) : rawValue
    onChange(field, value)
    setLinkedCustomer(null)
    setActiveField(field)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      searchCustomers(value)
    }, DEBOUNCE_MS)
  }

  const selectCustomer = (customer) => {
    // Fill all fields from the customer record
    onChange('customerName', customer.name || '')
    onChange('customerPhone', customer.phone || '')
    onChange('customerEmail', customer.email || '')
    onChange('customerCity', customer.city || '')
    onChange('customerState', customer.state || '')
    setLinkedCustomer(customer)
    setSuggestions([])
    setShowDropdown(false)
    setCustomerHighlight(-1)
    if (onCustomerLoaded) onCustomerLoaded(customer)
  }

  const handleCustomerKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setCustomerHighlight(i => (i + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setCustomerHighlight(i => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      if (customerHighlight >= 0) {
        e.preventDefault()
        selectCustomer(suggestions[customerHighlight])
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
      setCustomerHighlight(-1)
    }
  }

  // eslint-disable-next-line no-unused-vars
  const clearLinked = () => {
    setLinkedCustomer(null)
    onChange('customerName', '')
    onChange('customerPhone', '')
    onChange('customerEmail', '')
    onChange('customerCity', '')
    onChange('customerState', '')
  }

  const inputCls = `w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:bg-white transition`

  // compact=true: stacked 2-col layout (Name full-width, Phone+Email, City+State)
  // compact=false (default): wide 5-col horizontal grid
  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <div className="space-y-3">

          {/* Name — full width, primary field */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
              <User size={11} /> Name <span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={customerName}
                onChange={e => handleInput('customerName', e.target.value)}
                onKeyDown={handleCustomerKeyDown}
                className={inputCls}
                placeholder="Customer name (required)"
                required
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {isSearching && activeField === 'customerName' && (
                <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
              )}
            </div>
          </div>

          {/* Phone + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Phone size={11} /> Phone
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={customerPhone}
                  onChange={e => handleInput('customerPhone', e.target.value)}
                  onKeyDown={handleCustomerKeyDown}
                  className={inputCls}
                  placeholder="Phone number"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                {isSearching && activeField === 'customerPhone' && (
                  <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                )}
              </div>
            </div>

            <div className="relative">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Mail size={11} /> Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={customerEmail}
                  onChange={e => handleInput('customerEmail', e.target.value)}
                  onKeyDown={handleCustomerKeyDown}
                  className={inputCls}
                  placeholder="Email address"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
                {isSearching && activeField === 'customerEmail' && (
                  <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
                )}
              </div>
            </div>
          </div>

          {/* City / State — merged, typeahead */}
          <div className="relative" ref={locationDropdownRef}>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-1">
              <MapPin size={11} /> City / State
            </label>
            <input
              type="text"
              value={locationText}
              onChange={e => handleLocationInput(e.target.value)}
              onBlur={handleLocationBlur}
              onKeyDown={handleLocationKeyDown}
              className={inputCls}
              placeholder="Start typing a city…"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {showLocationDropdown && locationSuggestions.length > 0 && (
              <div className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                {locationSuggestions.map((loc, i) => (
                  <button
                    key={`${loc.city}-${loc.state}-${i}`}
                    type="button"
                    onMouseDown={() => selectLocation(loc)}
                    className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 transition-colors ${i === locationHighlight ? 'bg-amber-100' : 'hover:bg-amber-50'}`}
                  >
                    {loc.city}, {loc.state}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Suggestions dropdown */}
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 font-semibold flex items-center gap-1">
              <Search size={11} /> Matching customers — click to load
            </div>
            {suggestions.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => selectCustomer(c)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b last:border-0 transition-colors ${i === customerHighlight ? 'bg-amber-100' : 'hover:bg-amber-50'}`}
              >
                <div className="bg-amber-100 rounded-full p-1.5 shrink-0">
                  <User size={14} className="text-amber-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-800 truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {c.phone && <span className="mr-2">📱 {c.phone}</span>}
                    {c.email && <span>✉️ {c.email}</span>}
                  </p>
                  {(c.city || c.state) && (
                    <p className="text-xs text-gray-400">{[c.city, c.state].filter(Boolean).join(', ')}</p>
                  )}
                </div>
                <span className="text-xs text-amber-600 font-mono shrink-0">{c.customer_code}</span>
              </button>
            ))}
            <div className="px-3 py-2 bg-gray-50 border-t flex items-center gap-1 text-xs text-gray-400">
              <UserPlus size={11} />
              Not listed? Fill in the form to create a new record.
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>

      {/* Fields grid — wide 5-column layout */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

        {/* Customer Name */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
            <User size={11} /> Name *
          </label>
          <div className="relative">
            <input
              type="text"
              value={customerName}
              onChange={e => handleInput('customerName', e.target.value)}
              onKeyDown={handleCustomerKeyDown}
              className={inputCls}
              placeholder="Customer name"
              required
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {isSearching && activeField === 'customerName' && (
              <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Phone — primary lookup trigger */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
            <Phone size={11} /> Phone
          </label>
          <div className="relative">
            <input
              type="text"
              value={customerPhone}
              onChange={e => handleInput('customerPhone', e.target.value)}
              onKeyDown={handleCustomerKeyDown}
              className={inputCls}
              placeholder="Phone number"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
            {isSearching && activeField === 'customerPhone' && (
              <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Email — secondary lookup trigger */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
            <Mail size={11} /> Email
          </label>
          <div className="relative">
            <input
              type="email"
              value={customerEmail}
              onChange={e => handleInput('customerEmail', e.target.value)}
              onKeyDown={handleCustomerKeyDown}
              className={inputCls}
              placeholder="Email address"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {isSearching && activeField === 'customerEmail' && (
              <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
        </div>

        {/* City / State — merged, typeahead */}
        <div className="relative" ref={locationDropdownRef}>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
            <MapPin size={11} /> City / State
          </label>
          <input
            type="text"
            value={locationText}
            onChange={e => handleLocationInput(e.target.value)}
            onBlur={handleLocationBlur}
            onKeyDown={handleLocationKeyDown}
            className={inputCls}
            placeholder="Start typing a city…"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {showLocationDropdown && locationSuggestions.length > 0 && (
            <div className="absolute z-[9999] mt-1 w-full max-w-sm bg-white border border-gray-200 rounded shadow-lg">
              {locationSuggestions.map((loc, i) => (
                <button
                  key={`${loc.city}-${loc.state}-${i}`}
                  type="button"
                  onMouseDown={() => selectLocation(loc)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 ${i === locationHighlight ? 'bg-blue-100' : 'hover:bg-blue-50'}`}
                >
                  {loc.city}, {loc.state}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-[9999] mt-1 w-full max-w-sm bg-white border border-gray-200 rounded shadow-lg">
          <div className="px-3 py-1.5 bg-gray-50 border-b text-xs text-gray-500 font-medium">
            Matching customers — click to load
          </div>
          {suggestions.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => selectCustomer(c)}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left border-b last:border-0"
            >
              <div className="bg-blue-100 rounded-full p-1.5 shrink-0">
                <User size={14} className="text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-800 truncate">{c.name}</p>
                <p className="text-xs text-gray-500 truncate">
                  {c.phone && <span className="mr-2">📱 {c.phone}</span>}
                  {c.email && <span>✉️ {c.email}</span>}
                </p>
                {(c.city || c.state) && (
                  <p className="text-xs text-gray-400">{[c.city, c.state].filter(Boolean).join(', ')}</p>
                )}
              </div>
              <span className="text-xs text-blue-500 font-mono shrink-0">{c.customer_code}</span>
            </button>
          ))}
          <div className="px-3 py-1.5 bg-gray-50 border-t flex items-center gap-1 text-xs text-gray-400">
            <UserPlus size={11} />
            Not listed? Fill in the form — a new customer record will be created automatically.
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerLookup
